import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import Button from '@/components/button'
import LocalizedLink from '@/components/localized-link'
import Slider from '@/components/slider'
import {Text} from '@/components/text'
import type {CashbackCalculatorFragment} from '@/graphql-types'
import useMediaQuery from '@/hooks/use-media-query'
import type Locale from '@/locale/types'
import {useLocale} from '@/providers/locale'
import {Section, BackgroundWrapper, Layout} from '@/styles/grid'
import {breakpoints, headerSizes, radii, tokens} from '@/theme'
import currencyFormatter from '@/utils/currency-formatter'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import mq from '@/utils/media-query'

import {calculateCashback} from './calculate-cashback'
import {generateLocaleData} from './shared'

const mobileBreakpoint = 'sm'

export interface State {
    monthlySpend: number
    numOfEmployees: number
}

enum ActionType {
    MONTHLY_SPEND_UPDATED,
    NUM_OF_EMPLOYEES_UPDATED,
}

type Action = {
    type: ActionType.MONTHLY_SPEND_UPDATED | ActionType.NUM_OF_EMPLOYEES_UPDATED
    value: number
}

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case ActionType.MONTHLY_SPEND_UPDATED: {
            return {
                ...state,
                monthlySpend: action.value,
            }
        }

        case ActionType.NUM_OF_EMPLOYEES_UPDATED: {
            return {
                ...state,
                numOfEmployees: action.value,
            }
        }

        default: {
            throw new Error('Unrecognized action')
        }
    }
}

const initialState: State = {
    monthlySpend: 0,
    numOfEmployees: 0,
}

interface Props {
    content: CashbackCalculatorFragment
}

export const CashbackCalculator = ({content}: Props) => {
    const [state, dispatch] = React.useReducer(reducer, initialState)
    const locale = useLocale()

    if (!content) {
        return handleNoPrismicData()
    }

    return (
        <Wrapper as={Section} $variant="two-asymmetrical">
            <QuestionsSection state={state} dispatch={dispatch} content={content} locale={locale} />
            <ResultsSection
                cashback={calculateCashback(state, locale.code)}
                content={content}
                locale={locale}
            />
        </Wrapper>
    )
}

interface QuestionsSectionProps {
    state: State
    dispatch: React.Dispatch<Action>
    content: CashbackCalculatorFragment
    locale: Locale
}

const QuestionsSection = ({state, dispatch, content, locale}: QuestionsSectionProps) => {
    const isSmallScreen = useMediaQuery.max(mobileBreakpoint)
    const monthlySpend = content.data.monthly_spend[0]
    const numOfEmployees = content.data.num_of_employees[0]

    return (
        <Questions>
            <Text as="h2" $variant="heading-base">
                {content.data.title}
            </Text>

            <Question>
                <SliderWrapper>
                    <Text $variant="paragraph-base" $color="brand">
                        {monthlySpend.heading}
                    </Text>

                    <Slider
                        value={state.monthlySpend}
                        min={0}
                        max={generateLocaleData(locale.code).spend}
                        withNumberInput
                        label={monthlySpend.subheading}
                        onChange={(value) => {
                            dispatch({
                                type: ActionType.MONTHLY_SPEND_UPDATED,
                                value,
                            })
                        }}
                        icons={
                            isSmallScreen
                                ? undefined
                                : [
                                      monthlySpend.low_icon,
                                      monthlySpend.medium_icon,
                                      monthlySpend.high_icon,
                                  ]
                        }
                    />
                </SliderWrapper>
            </Question>

            <Question>
                <SliderWrapper>
                    <Text $variant="paragraph-base" $color="brand">
                        {numOfEmployees.heading}
                    </Text>

                    <Slider
                        value={state.numOfEmployees}
                        min={0}
                        max={500}
                        withNumberInput
                        label={numOfEmployees.subheading}
                        onChange={(value) => {
                            dispatch({
                                type: ActionType.NUM_OF_EMPLOYEES_UPDATED,
                                value,
                            })
                        }}
                        icons={
                            isSmallScreen
                                ? undefined
                                : [
                                      numOfEmployees.low_icon,
                                      numOfEmployees.medium_icon,
                                      numOfEmployees.high_icon,
                                  ]
                        }
                    />
                </SliderWrapper>
            </Question>
        </Questions>
    )
}

interface ResultsSectionProps {
    cashback: number
    content: CashbackCalculatorFragment
    locale: Locale
}

const ResultsSection = ({cashback, content, locale}: ResultsSectionProps) => {
    const formatCurrency = currencyFormatter(locale.code)

    return (
        <StickyWrapper>
            <ResultsSectionContainer>
                <Results $color="pink">
                    <ResultHeader forwardedAs="h3" $variant="spezia">
                        {content.data.results_title}
                    </ResultHeader>

                    <ResultLine>
                        <Text $variant="paragraph-large" as="h4">
                            {content.data.results_line_cashback}
                        </Text>

                        <ResultOutput $variant="heading-base" $color="muted" aria-hidden="true">
                            {cashback <= 0 ? '-' : formatCurrency(cashback)}
                        </ResultOutput>
                    </ResultLine>
                </Results>
                <Text $variant="label-base">{content.data.results_subheading}</Text>

                {content.data.cta_text && (
                    <Button as={LocalizedLink} to="/demo">
                        {content.data.cta_text}
                    </Button>
                )}
            </ResultsSectionContainer>
        </StickyWrapper>
    )
}

const Wrapper = styled(Layout)`
    display: grid;

    /* stylelint-disable-next-line */
    @media (min-width: ${breakpoints.sm}) and (max-height: ${breakpoints.sm}) {
        padding-top: ${tokens.spacing40};
        padding-bottom: ${tokens.spacing40};
    }

    ${mq.max(mobileBreakpoint)} {
        row-gap: ${tokens.spacing60};
    }
`

const StickyWrapper = styled.div`
    position: sticky;
    top: ${headerSizes.heightSmall}px;

    ${mq.min(mobileBreakpoint)} {
        top: ${headerSizes.heightLarge}px;
    }
`

const ResultsSectionContainer = styled.div`
    display: grid;
    align-content: start;
    row-gap: ${tokens.spacing40};
    height: 100%;

    ${mq.min(mobileBreakpoint)} {
        justify-items: start;
    }
`

const ResultHeader = styled(Text)`
    ${mq.max(mobileBreakpoint)} {
        text-align: center;
    }
`

const Results = styled(BackgroundWrapper)`
    display: grid;
    width: 100%;
    padding: ${tokens.spacing40};
    overflow: hidden;
    border-radius: ${radii.arc20};
    gap: ${tokens.spacing32};
    grid-template-columns: 1fr;

    ${mq.max(mobileBreakpoint)} {
        padding: ${tokens.spacing20};
    }

    /* stylelint-disable-next-line */
    @media (min-width: ${breakpoints.sm}) and (max-height: ${breakpoints.sm}) {
        padding: ${tokens.spacing20};
    }
`

const ResultLine = styled.div`
    display: grid;
    grid-template-columns: repeat(2, auto);
    justify-content: space-between;
    align-items: center;
    gap: ${tokens.spacing10};

    ${mq.max('md')} {
        grid-template-columns: 1fr;
        justify-items: left;
        gap: ${tokens.spacing10};
    }

    /* stylelint-disable-next-line */
    ${mq.max(mobileBreakpoint)} {
        justify-items: center;
    }
`

const ResultOutput = styled(Text)`
    white-space: nowrap;
`

const Questions = styled.div`
    display: grid;
    grid-template-rows: 1fr auto;
    row-gap: ${tokens.spacing60};
    height: 100%;
`

const Question = styled.div`
    display: grid;
    row-gap: ${tokens.spacing4};
`

const SliderWrapper = styled.div`
    display: grid;
    row-gap: ${tokens.spacing4};
`

export const cashbackCalculatorFragment = graphql`
    fragment CashbackCalculator on PrismicCashbackCalculator {
        type
        data {
            title
            results_title
            results_line_cashback
            results_subheading
            cta_text
            monthly_spend {
                heading
                subheading
                low_icon {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                medium_icon {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                high_icon {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
            }
            num_of_employees {
                heading
                subheading
                low_icon {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                medium_icon {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                high_icon {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
            }
        }
    }
`
